import { create } from 'zustand';
import { persist } from 'zustand/middleware';
import axios from 'axios';

const API_BASE_URL = process.env.REACT_APP_API_URL || 'https://api.daily2k.ng/api';

interface AdminUser {
  id: string;
  email: string;
  username?: string;
  first_name: string;
  last_name: string;
  phone_number?: string;
  role: 'admin' | 'moderator' | 'support';
  is_active: boolean;
  avatar_url?: string;
}

interface AuthState {
  user: AdminUser | null;
  token: string | null;
  isAuthenticated: boolean;
  isInitialized: boolean;

  login: (email: string, password: string) => Promise<void>;
  logout: () => void;
  initialize: () => Promise<void>;
  setUser: (user: AdminUser | null) => void;
}

// Helper: fetch the real admin identity from the backend
async function fetchAdminMe(token: string): Promise<AdminUser | null> {
  try {
    const response = await axios.get(`${API_BASE_URL}/admin/me/`, {
      headers: { Authorization: `Bearer ${token}` },
    });
    return response.data?.data?.user ?? null;
  } catch {
    return null;
  }
}

export const useAuthStore = create<AuthState>()(
  persist(
    (set) => ({
      user: null,
      token: null,
      isAuthenticated: false,
      isInitialized: false,

      initialize: async () => {
        try {
          const token = localStorage.getItem('adminToken');
          if (!token) {
            set({ isInitialized: true });
            return;
          }

          // Fetch real user identity — never use hardcoded fallback
          const user = await fetchAdminMe(token);
          if (user) {
            set({
              user,
              token,
              isAuthenticated: true,
              isInitialized: true,
            });
          } else {
            // Token invalid or expired
            localStorage.removeItem('adminToken');
            localStorage.removeItem('adminRefreshToken');
            set({ user: null, token: null, isAuthenticated: false, isInitialized: true });
          }
        } catch {
          localStorage.removeItem('adminToken');
          localStorage.removeItem('adminRefreshToken');
          set({ isInitialized: true });
        }
      },

      login: async (email: string, password: string) => {
        const response = await axios.post(`${API_BASE_URL}/admin/login/`, {
          email,
          password,
        });

        const { data } = response.data;
        const token = data?.token || data?.access_token;
        const refreshToken = data?.refresh_token;

        if (!token) {
          throw new Error('No token in response');
        }

        localStorage.setItem('adminToken', token);
        if (refreshToken) {
          localStorage.setItem('adminRefreshToken', refreshToken);
        }

        // Use real user from login response — backend returns actual user data
        const user: AdminUser = data?.user ?? null;
        if (!user) {
          throw new Error('No user data in login response');
        }

        set({ user, token, isAuthenticated: true });
      },

      logout: () => {
        localStorage.removeItem('adminToken');
        localStorage.removeItem('adminRefreshToken');
        set({
          user: null,
          token: null,
          isAuthenticated: false,
        });
      },

      setUser: (user) => {
        set({ user });
      },
    }),
    {
      name: 'admin-auth-storage',
      partialize: (state) => ({
        token: state.token,
        user: state.user,
        isAuthenticated: state.isAuthenticated,
      }),
    }
  )
);
